import i18n from '@/utils/i18n';
import { type FC, useCallback } from 'react';
import {
  InboundListFilter,
  type OrderInboundsOrderListFilterFormValues,
} from './types';
import { OrderInboundsOrderListFilterFormSearchInput } from './order-inbounds-order-list-filter-form-search-input';
import { OrderInboundsOrderListFilterPanel } from './order-inbounds-order-list-filter-panel';
import { PillButton } from '@usepepper/ui/atoms/pill-button';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';

interface Props {
  containerStyles?: StyleProp<ViewStyle>;
  onChange: (values: OrderInboundsOrderListFilterFormValues) => void;
  statusCounts: {
    closed: number;
    open: number;
  };
}

export const OrderInboundsOrderListFilterForm: FC<Props> = ({
  containerStyles,
  onChange,
  statusCounts,
}) => {
  const activeStatusFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );
  const appliedSearchTerm = useOrderInboundsStore(
    (store) => store.queryFilters.searchTerm,
  );

  const handleFilterPress = useCallback(
    (filter: InboundListFilter) => {
      onChange({ filter, search: appliedSearchTerm });
    },
    [onChange, appliedSearchTerm],
  );

  const handleSearchSubmit = useCallback(
    (search: string) => {
      onChange({ filter: activeStatusFilter, search });
    },
    [onChange, activeStatusFilter],
  );

  return (
    <View style={[styles.container, containerStyles]}>
      <View style={styles.searchRow}>
        <View style={styles.searchInput}>
          <OrderInboundsOrderListFilterFormSearchInput
            appliedSearchTerm={appliedSearchTerm}
            onSubmit={handleSearchSubmit}
          />
        </View>
        <OrderInboundsOrderListFilterPanel />
      </View>

      <View style={[styles.filterButtonsContainer]}>
        <PillButton
          button={{
            active: activeStatusFilter === InboundListFilter.Open,
            count: statusCounts.open,
            label: i18n.t('order_inbound.filter.open'),
            onPress: () => handleFilterPress(InboundListFilter.Open),
          }}
        />

        <PillButton
          button={{
            active: activeStatusFilter === InboundListFilter.Closed,
            count: statusCounts.closed,
            label: i18n.t('order_inbound.filter.closed'),
            onPress: () => handleFilterPress(InboundListFilter.Closed),
          }}
          variant="medium"
        />

        <PillButton
          button={{
            active: activeStatusFilter === InboundListFilter.All,
            label: i18n.t('order_inbound.filter.all'),
            onPress: () => handleFilterPress(InboundListFilter.All),
          }}
          variant="medium"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  filterButtonsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  searchInput: {
    flex: 1,
  },
  searchRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
});
